import type { CustomAttributeData } from '@risksmart-app/domain/src/types/common.types';
import type { InsertIssueUpdateMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from 'src/utils/trpc';

type InsertIssueUpdateInput = {
  ParentIssueId: string;
  Title: string;
  Description: string;
  CustomAttributeData?: CustomAttributeData | null;
};

/**
 * Maps TRPC mutation response to match the GraphQL mutation structure
 */
function mapTrpcResponseToGraphQL(trpcData: {
  Id: string;
}): InsertIssueUpdateMutation {
  return {
    insert_issue_update_one: {
      __typename: 'issue_update',
      Id: trpcData.Id,
    },
  };
}

export const useInsertIssueUpdateTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    ...trpc.frontend.issueUpdate.insert.mutationOptions({
      onSuccess: async () => {
        // Invalidate issue update queries to refresh data
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.issueUpdate.getById.queryKey(),
        });
      },
    }),
  });

  return {
    insertIssueUpdate: async (
      variables: InsertIssueUpdateInput
    ): Promise<InsertIssueUpdateMutation> => {
      const result = await mutation.mutateAsync(variables);

      return mapTrpcResponseToGraphQL(result);
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
